import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Typography, Divider, Button, Chip, Paper, Skeleton } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { FullCardSkeleton } from "./_PortalSkeletons";
import { useActAsClient } from "./ActAsClientContext";

import { auth } from "../../firebase-config";        // ✅ MAIN APP AUTH
import { authPortal } from "../../firebase-portal";  // ✅ PORTAL AUTH (magic link)

const GET_ALL_DOCS_URL =
  process.env.REACT_APP_GET_ALL_CLIENT_DOCS_URL ||
  "https://getclientdocuments-774073159618.us-central1.run.app";

export default function ClientPortalDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const actAs = useActAsClient();
  const [params] = useSearchParams();

  const tsToLocale = (ts) =>
    ts?.toDate
      ? ts.toDate().toLocaleString()
      : ts?._seconds
      ? new Date(ts._seconds * 1000).toLocaleString()
      : ts
      ? new Date(ts).toLocaleString()
      : "Unknown";

  useEffect(() => {
    // If URL expects act-as, wait until context has been hydrated from URL/sessionStorage
    const isExpectingActAs = params.has("actAsClient") || params.has("as");
    if (isExpectingActAs && !actAs.isActingAs) return;

    const run = async () => {
      setLoading(true);
      try {
        // ✅ KEY CHANGE:
        // - Staff act-as uses MAIN auth (persisted across tabs)
        // - Real client uses PORTAL auth (magic link session)
        const shouldUseStaffAuth = Boolean(actAs?.isActingAs);
        const user = shouldUseStaffAuth ? auth.currentUser : authPortal.currentUser;

        if (!user) {
          throw new Error(shouldUseStaffAuth ? "No staff user" : "No portal user");
        }

        const idToken = await user.getIdToken(true);

        const actAsHeaders = actAs.getActAsHeaders();
        const headers = { Authorization: `Bearer ${idToken}`, ...actAsHeaders };

        const { data } = await axios.post(GET_ALL_DOCS_URL, {}, { headers });
        setDocs(Array.isArray(data?.documents) ? data.documents : []);
      } catch (e) {
        console.error("Load documents failed:", e);
        setDocs([]);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [actAs, params]);

  return (
    <Box>
      {/* Staff "Acting as" banner */}
      {actAs?.isActingAs && (
        <Box
          sx={{
            mb: 2,
            p: 1.25,
            borderRadius: 1,
            bgcolor: "#FFFBEB",
            border: "1px solid #FCD34D",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#92400E" }}>
            Staff preview: acting as{" "}
            <span style={{ textDecoration: "underline" }}>
              {actAs.actAsName || actAs.clientId}
            </span>
            . All actions are recorded.
          </Typography>
        </Box>
      )}

      <div className="flex items-center gap-2">
        <Typography variant="h5" sx={{ mb: 2 }}>
          Documents
        </Typography>
        {loading ? (
          <Skeleton variant="rounded" width={42} height={24} />
        ) : (
          <Chip
            size="small"
            label={`${docs?.length || 0}`}
            color="primary"
            variant="outlined"
          />
        )}
      </div>

      <Divider sx={{ mb: 2 }} />

      {loading && (
        <Box>
          {Array.from({ length: 6 }).map((_, i) => (
            <FullCardSkeleton key={`doc_skel_${i}`} rows={4} />
          ))}
        </Box>
      )}

      {!loading && docs.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No documents found.
        </Typography>
      )}

      {!loading && docs.length > 0 && (
        <Box>
          {docs.map((doc, idx) => {
            const link = doc?.url || doc?.downloadUrl;

            const rawDue = doc?.dueDate;
            const dueMs =
              rawDue && typeof rawDue === "object" && "_seconds" in rawDue
                ? rawDue._seconds * 1000
                : rawDue
                ? new Date(rawDue).getTime()
                : null;

            const hasValidDue = Number.isFinite(dueMs);
            const overdue = hasValidDue ? dueMs < Date.now() : false;

            const chipStyles = overdue
              ? {
                  backgroundColor: "#FEE2E2",
                  color: "#991B1B",
                  border: "1px solid #FCA5A5",
                }
              : {
                  backgroundColor: "#E0E7FF",
                  color: "#1E3A8A",
                  border: "1px solid #C7D2FE",
                };

            return (
              <Paper
                key={`${doc?.id || doc?.documentId || idx}`}
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div className="flex-1">
                  <div className="font-semibold">{doc?.name || "Document"}</div>
                  <div className="text-xs text-gray-500">
                    Assigned: {tsToLocale(doc?.assignedAt) || "Unknown"}
                  </div>

                  {hasValidDue && (
                    <span
                      title={new Date(dueMs).toLocaleString()}
                      style={{
                        display: "inline-block",
                        marginTop: 6,
                        borderRadius: 12,
                        padding: "2px 8px",
                        fontSize: 12,
                        fontWeight: 600,
                        ...chipStyles,
                      }}
                    >
                      {overdue ? "Overdue" : "Due"} {new Date(dueMs).toLocaleDateString()}
                    </span>
                  )}
                </div>

                <Button
                  size="small"
                  variant="outlined"
                  disabled={!link}
                  onClick={() => link && window.open(link, "_blank")}
                >
                  View
                </Button>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}